import { ColumnDefResolved, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table"
import { useEffect } from "react"

import getObjectTableViewConfigStorageKey from "@utils/getObjectTableViewConfigStorageKey"

import useObjectsView from "@organisms/ObjectsView/ObjectsView.context"
import { ObjectsData } from "@organisms/ObjectsView/ObjectsView.types"
import Table from "@organisms/Table/Table"

import { TableViewContext } from "./TableView.context"
import { TableViewConfig, TableViewContextValues, TableViewProviderProps } from "./TableView.types"
import useTableColumns from "./hooks/useTableColumns"
import useTableViewConfig from "./hooks/useTableViewConfig"

export default function TableViewProvider<T = ObjectsData>(props: TableViewProviderProps) {
    const { children, defaultTableConfigOverride } = props

    const { data, onSortingChange, objectType, objectTypeVariation, activePhase, activeTab } = useObjectsView<T>()

    const { currentTableViewConfig, updateCurrentTableViewConfig } = useTableViewConfig(
        getObjectTableViewConfigStorageKey({
            objectType: objectType,
            objectTypeVariation: objectTypeVariation,
            phase: activePhase,
            tab: activeTab?.title ?? "",
        }),
        defaultTableConfigOverride,
    )

    const {
        setColumns,
        tableColumns,
        reachedPinLimit,
        updateColumnVisibility,
        unpinColumn,
        pinColumn,
        updateColumnPinningOrder,
        updateColumnOrder,
        invisibleColumns,
        reachedMinimumOfColumnsVisible,
        pinnedColumns,
        unpinnedColumns,
        columnsNamesList,
        visibleColumns,
        activeSortingColumn,
        activeSorting,
        sortingEnabledColumns,
        activeSortingIcons,
        activeSortingLabels,
    } = useTableColumns<T>({
        currentTableViewConfig: currentTableViewConfig as TableViewConfig,
        updateCurrentTableViewConfig,
    })

    const table = useReactTable({
        columns: tableColumns,
        data,
        getCoreRowModel: getCoreRowModel(),
        initialState: !currentTableViewConfig
            ? undefined
            : {
                  sorting: currentTableViewConfig?.sorting,
                  columnVisibility: currentTableViewConfig?.columnVisibility,
                  columnPinning: currentTableViewConfig?.columnPinning,
                  columnOrder: currentTableViewConfig?.columnOrder,
              },
        onSortingChange,
        getSortedRowModel: getSortedRowModel(),
        manualSorting: true,
        debugHeaders: true,
        debugColumns: true,
    })

    useEffect(() => {
        if (currentTableViewConfig) {
            const newColumnPinning = [...(currentTableViewConfig.columnPinning?.left ?? [])]

            table.setState((state) => {
                return {
                    ...state,
                    ...currentTableViewConfig,
                    columnPinning: {
                        left: newColumnPinning,
                    },
                }
            })
        }
    }, [currentTableViewConfig])

    const contextValue: TableViewContextValues<T> = {
        columns: tableColumns as ColumnDefResolved<T>[],
        setColumns,
        table,
        columnsOrder: currentTableViewConfig?.columnOrder ?? [],
        invisibleColumns,
        updateColumnVisibility,
        reachedPinLimit,
        reachedMinimumOfColumnsVisible,
        updateColumnOrder,
        pinColumn,
        unpinColumn,
        updateColumnPinningOrder,
        pinnedColumns,
        unpinnedColumns,
        columnsNamesList,
        currentTableViewConfig,
        visibleColumns: visibleColumns as ColumnDefResolved<T>[],
        activeSorting,
        activeSortingColumn,
        sortingEnabledColumns,
        activeSortingIcons,
        activeSortingLabels,
        defaultTableConfigOverride,
    }

    return (
        <TableViewContext.Provider value={contextValue}>
            <Table.ColumnRegistry<T> setColumns={setColumns}>{children}</Table.ColumnRegistry>
        </TableViewContext.Provider>
    )
}
